import type { FimRevisionDiffDetailEntry } from 'features/knowledgeDocumentViewer/hooks/useRevisionDiffDetails'

import type { RevisionComparePreview } from './previewDocumentHandler'

function pairKey({ data }: FimRevisionDiffDetailEntry): string {
  return [
    data.from_document_id,
    data.from_page_num,
    data.to_document_id,
    data.to_page_num,
  ].join('|')
}

function isNewer(
  candidate: FimRevisionDiffDetailEntry,
  best: FimRevisionDiffDetailEntry
): boolean {
  const candidateTs = candidate.data.generated_at
  const bestTs = best.data.generated_at
  if (candidateTs === null || candidateTs === undefined) return false
  if (bestTs === null || bestTs === undefined) return true
  return candidateTs > bestTs
}

/**
 * Resolves the one detail a doc-chat card refers to. Pages only constrain the
 * match when the card carries them. Re-published audits of the same page pair
 * collapse to the newest by `generated_at`; zero matches or several distinct
 * page pairs return undefined so the caller never guesses.
 */
export function resolveRevisionPairDetail(
  details: readonly FimRevisionDiffDetailEntry[],
  revision: RevisionComparePreview
): FimRevisionDiffDetailEntry | undefined {
  const newestByPair = new Map<string, FimRevisionDiffDetailEntry>()
  for (const detail of details) {
    const { data } = detail
    const matches =
      data.from_document_id === revision.fromDocumentId &&
      data.to_document_id === revision.toDocumentId &&
      (revision.fromPageNum === null ||
        data.from_page_num === revision.fromPageNum) &&
      (revision.toPageNum === null || data.to_page_num === revision.toPageNum)
    if (!matches) continue
    const key = pairKey(detail)
    const best = newestByPair.get(key)
    if (!best || isNewer(detail, best)) newestByPair.set(key, detail)
  }
  if (newestByPair.size !== 1) return undefined
  return [...newestByPair.values()][0]
}
